import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Typography, IconButton, Tooltip, TablePagination, TextField,
  MenuItem, Select, FormControl, InputLabel, Stack, Checkbox, Button,
  Avatar, InputAdornment, Divider,
} from '@mui/material';
import {
  Visibility, PersonAdd, FilterList, Search as SearchIcon,
  Clear as ClearIcon, RestartAlt as ResetIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { getAllUsers } from '../../../api/userAPI';

const STATUS_COLORS = {
  Upcoming:  'info',
  Running:   'success',
  Completed: 'default',
  Cancelled: 'error',
  'On Hold': 'warning',
};

const PAYMENT_COLORS = {
  Paid:    'success',
  Partial: 'warning',
  Overdue: 'error',
  Pending: 'default',
};

// Allowed executive roles for the filter dropdown
const ALLOWED_EXECUTIVE_ROLES = [
  'operation manager',
  'operation',
  'operation executive',
  'sales manager',
  'sales executive',
  'sales team lead',
];

const isAllowedExecutiveRole = (role) => {
  if (!role) return false;
  return ALLOWED_EXECUTIVE_ROLES.includes(String(role).toLowerCase().trim());
};

const TripsTable = ({
  trips = [],
  pagination = {},
  loading,
  currentStatus = 'All',
  onPageChange,
  onStatusFilter,
  onRowClick,
  onAssign,
  showAssign = true,
  selectedIds = [],
  onSelectChange,
  onBulkAssign,
  users = null,
}) => {
  // ── Filters State ──
  const [statusFilter, setStatusFilter] = useState(currentStatus || 'All');
  const [executiveFilter, setExecutiveFilter] = useState('All');
  const [paymentFilter, setPaymentFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [travelStartDate, setTravelStartDate] = useState('');
  const [travelEndDate, setTravelEndDate] = useState('');

  // ── Pagination State ──
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pagination?.limit || 20);

  // ── Users list for executive filter ──
  const [usersList, setUsersList] = useState([]);

  useEffect(() => {
    if (!users) {
      getAllUsers()
        .then(res => {
          if (res?.data && Array.isArray(res.data)) {
            setUsersList(res.data.filter(u => u.isActive !== false));
          }
        })
        .catch(() => {});
    }
  }, [users]);

  // Synchronize statusFilter if currentStatus prop changes (e.g. KPI card clicked)
  useEffect(() => {
    if (currentStatus) {
      setStatusFilter(currentStatus);
      setPage(0);
    }
  }, [currentStatus]);

  // ── Extract unique executives from trips and user roster ──
  // ONLY Operation and Sales roles: Operation Manager, Operation, Sales Manager, Sales Executive, Sales Team Lead
  const executiveOptions = useMemo(() => {
    const allKnownUsers = users || usersList;
    const eligibleRoster = allKnownUsers.filter(u => isAllowedExecutiveRole(u.permission));

    const execMap = new Map();

    // 1. Pre-populate all eligible users so active sales & operation staff are visible
    eligibleRoster.forEach(u => {
      const id = String(u._id || u.id);
      if (id && u.fullName) {
        execMap.set(id, { id, name: u.fullName, count: 0 });
      }
    });

    // 2. Count trips assigned to eligible users
    (trips || []).forEach(trip => {
      const tripExecs = [];
      trip.allocation?.forEach(a => {
        const u = a.user_id;
        const id = u?._id || u?.id || (typeof u === 'string' ? u : null);
        const name = u?.fullName || (typeof u === 'string' ? u : null);
        if (id) tripExecs.push({ id: String(id), name });
      });
      trip.manage_teams?.forEach(mt => {
        const u = mt.user_id;
        const id = u?._id || u?.id || (typeof u === 'string' ? u : null);
        const name = u?.fullName || mt.fullName;
        if (id) tripExecs.push({ id: String(id), name });
      });

      const seen = new Set();
      tripExecs.forEach(({ id, name }) => {
        let targetId = null;
        if (execMap.has(id)) {
          targetId = id;
        } else if (name) {
          const matched = eligibleRoster.find(
            eu => eu.fullName?.toLowerCase().trim() === name.toLowerCase().trim()
          );
          if (matched) {
            targetId = String(matched._id || matched.id);
          }
        }

        if (targetId && !seen.has(targetId)) {
          seen.add(targetId);
          if (execMap.has(targetId)) {
            execMap.get(targetId).count += 1;
          }
        }
      });
    });

    return Array.from(execMap.values()).sort(
      (a, b) => b.count - a.count || a.name.localeCompare(b.name)
    );
  }, [trips, users, usersList]);

  // Unassigned trips count
  const unassignedCount = useMemo(() => {
    return (trips || []).filter(t => {
      const hasAlloc = Array.isArray(t.allocation) && t.allocation.length > 0;
      const hasMt = Array.isArray(t.manage_teams) && t.manage_teams.length > 0;
      return !hasAlloc && !hasMt;
    }).length;
  }, [trips]);

  // Status counts
  const statusCounts = useMemo(() => {
    const counts = { All: (trips || []).length };
    (trips || []).forEach(t => {
      const s = t.trip_status || 'Other';
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [trips]);

  // Payment counts
  const paymentCounts = useMemo(() => {
    const counts = { All: (trips || []).length };
    (trips || []).forEach(t => {
      const p = t.payment?.status || 'Pending';
      counts[p] = (counts[p] || 0) + 1;
    });
    return counts;
  }, [trips]);

  // ── Filtered Trips ──
  const filteredTrips = useMemo(() => {
    return (trips || []).filter(trip => {
      // 1. Status Filter
      if (statusFilter !== 'All' && trip.trip_status !== statusFilter) {
        return false;
      }

      // 2. Executive Filter
      if (executiveFilter !== 'All') {
        if (executiveFilter === 'unassigned') {
          const hasAlloc = Array.isArray(trip.allocation) && trip.allocation.length > 0;
          const hasMt = Array.isArray(trip.manage_teams) && trip.manage_teams.length > 0;
          if (hasAlloc || hasMt) return false;
        } else {
          const selectedExec = executiveOptions.find(e => e.id === executiveFilter);
          const execName = selectedExec?.name;

          const matchesAlloc = trip.allocation?.some(a => {
            const uid = a.user_id?._id || a.user_id?.id || (typeof a.user_id === 'string' ? a.user_id : null);
            const fullName = a.user_id?.fullName || (typeof a.user_id === 'string' ? a.user_id : null);
            return String(uid) === String(executiveFilter) || (execName && fullName === execName);
          });

          const matchesMt = trip.manage_teams?.some(mt => {
            const uid = mt.user_id?._id || mt.user_id?.id || (typeof mt.user_id === 'string' ? mt.user_id : null);
            const fullName = mt.user_id?.fullName || mt.fullName;
            return String(uid) === String(executiveFilter) || (execName && fullName === execName);
          });

          if (!matchesAlloc && !matchesMt) return false;
        }
      }

      // 3. Payment Filter
      if (paymentFilter !== 'All' && (trip.payment?.status || 'Pending') !== paymentFilter) {
        return false;
      }

      // 4. Search Filter (Guest Name, Email, Phone, Destination, Booking Ref)
      if (searchTerm.trim()) {
        const q = searchTerm.trim().toLowerCase();
        const guestName = (trip.guest_id?.name || '').toLowerCase();
        const guestEmail = (trip.guest_id?.email || '').toLowerCase();
        const guestPhone = (trip.guest_id?.phone || '').toLowerCase();
        const bookingRef = String(trip.booking_ref || trip._id || '').toLowerCase();
        const dest = (trip.destination || '').toLowerCase();

        const matches =
          guestName.includes(q) ||
          guestEmail.includes(q) ||
          guestPhone.includes(q) ||
          bookingRef.includes(q) ||
          dest.includes(q);

        if (!matches) return false;
      }

      // 5. Travel Date Filter
      if (travelStartDate || travelEndDate) {
        if (!trip.travel_date) return false;
        const tripDate = dayjs(trip.travel_date).format('YYYY-MM-DD');
        if (travelStartDate && tripDate < travelStartDate) return false;
        if (travelEndDate && tripDate > travelEndDate) return false;
      }

      return true;
    });
  }, [trips, statusFilter, executiveFilter, paymentFilter, searchTerm, travelStartDate, travelEndDate, executiveOptions]);

  // ── Paginated Trips ──
  const paginatedTrips = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredTrips.slice(start, start + rowsPerPage);
  }, [filteredTrips, page, rowsPerPage]);

  // Adjust page if out of bounds after filtering
  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= filteredTrips.length) {
      setPage(0);
    }
  }, [filteredTrips.length, page, rowsPerPage]);

  // ── Handlers ──
  const handleStatusChange = (e) => {
    const next = e.target.value;
    setStatusFilter(next);
    setPage(0);
    onStatusFilter?.(next);
  };

  const handleExecutiveChange = (e) => {
    setExecutiveFilter(e.target.value);
    setPage(0);
  };

  const handlePaymentChange = (e) => {
    setPaymentFilter(e.target.value);
    setPage(0);
  };

  const handleResetAllFilters = () => {
    setStatusFilter('All');
    setExecutiveFilter('All');
    setPaymentFilter('All');
    setSearchTerm('');
    setTravelStartDate('');
    setTravelEndDate('');
    setPage(0);
    onStatusFilter?.('All');
  };

  const hasActiveFilters =
    statusFilter !== 'All' ||
    executiveFilter !== 'All' ||
    paymentFilter !== 'All' ||
    searchTerm.trim() !== '' ||
    travelStartDate !== '' ||
    travelEndDate !== '';

  const selectedExecutiveName = useMemo(() => {
    if (executiveFilter === 'unassigned') return 'Unassigned';
    const found = executiveOptions.find(e => e.id === executiveFilter);
    return found ? found.name : executiveFilter;
  }, [executiveFilter, executiveOptions]);

  // Selection
  const isSelected = (id) => selectedIds.includes(id);
  const toggleSelect = (id) => {
    const next = isSelected(id)
      ? selectedIds.filter(s => s !== id)
      : [...selectedIds, id];
    onSelectChange?.(next);
  };

  const toggleAll = () => {
    const filteredIds = filteredTrips.map(t => t._id);
    const allSelected = filteredIds.length > 0 && filteredIds.every(id => selectedIds.includes(id));
    if (allSelected) {
      onSelectChange?.(selectedIds.filter(id => !filteredIds.includes(id)));
    } else {
      const combined = Array.from(new Set([...selectedIds, ...filteredIds]));
      onSelectChange?.(combined);
    }
  };

  return (
    <Box>
      {/* ── Toolbar & Filter Controls ── */}
      <Box sx={{ mb: 2 }}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 1.5 }}
        >
          {/* Guest Name / Email / Phone Search */}
          <TextField
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            placeholder="Search by Guest Name, Email, Phone, Destination, Ref..."
            sx={{
              flex: { xs: '1 1 100%', sm: '1 1 280px', md: '1 1 340px' },
              maxWidth: { xs: '100%', md: 400 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => { setSearchTerm(''); setPage(0); }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* Executive User Filter */}
          {showAssign !== false && (
            <FormControl size="small" sx={{ minWidth: 200, flex: { xs: '1 1 160px', sm: 'none' } }}>
              <InputLabel id="executive-filter-label">Executive</InputLabel>
              <Select
                labelId="executive-filter-label"
                value={executiveFilter}
                label="Executive"
                onChange={handleExecutiveChange}
                renderValue={(selected) => {
                  if (selected === 'All') return 'All Executives';
                  if (selected === 'unassigned') return `Unassigned (${unassignedCount})`;
                  return selectedExecutiveName;
                }}
              >
                <MenuItem value="All">
                  <Typography variant="body2" fontWeight={600}>All Executives</Typography>
                </MenuItem>
                <MenuItem value="unassigned">
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Typography variant="body2" color="text.secondary">Unassigned Trips</Typography>
                    <Chip label={unassignedCount} size="small" color={unassignedCount > 0 ? 'warning' : 'default'} sx={{ height: 20, fontSize: 11 }} />
                  </Box>
                </MenuItem>
                <Divider />
                {executiveOptions.map(exec => (
                  <MenuItem key={exec.id} value={exec.id}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1.5}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{ width: 22, height: 22, fontSize: 11, bgcolor: 'primary.main' }}>
                          {exec.name[0]}
                        </Avatar>
                        <Typography variant="body2">{exec.name}</Typography>
                      </Box>
                      <Chip label={exec.count} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 160, flex: { xs: '1 1 140px', sm: 'none' } }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label="Status"
              onChange={handleStatusChange}
            >
              {['All', 'Upcoming', 'Running', 'Completed', 'Cancelled', 'On Hold'].map(s => (
                <MenuItem key={s} value={s}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Typography variant="body2">{s}</Typography>
                    {statusCounts[s] !== undefined && (
                      <Chip label={statusCounts[s]} size="small" variant="outlined" sx={{ height: 18, fontSize: 10, ml: 1 }} />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Payment Status Filter */}
          <FormControl size="small" sx={{ minWidth: 150, flex: { xs: '1 1 130px', sm: 'none' } }}>
            <InputLabel id="payment-filter-label">Payment</InputLabel>
            <Select
              labelId="payment-filter-label"
              value={paymentFilter}
              label="Payment"
              onChange={handlePaymentChange}
            >
              {['All', 'Paid', 'Partial', 'Overdue', 'Pending'].map(p => (
                <MenuItem key={p} value={p}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Typography variant="body2">{p}</Typography>
                    {paymentCounts[p] !== undefined && (
                      <Chip label={paymentCounts[p]} size="small" variant="outlined" sx={{ height: 18, fontSize: 10, ml: 1 }} />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Travel Date From */}
          <TextField
            size="small"
            type="date"
            label="Travel From"
            value={travelStartDate}
            onChange={(e) => {
              setTravelStartDate(e.target.value);
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', sm: 145 }, flex: { xs: '1 1 140px', sm: 'none' } }}
          />

          {/* Travel Date To */}
          <TextField
            size="small"
            type="date"
            label="Travel To"
            value={travelEndDate}
            onChange={(e) => {
              setTravelEndDate(e.target.value);
              setPage(0);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: '100%', sm: 145 }, flex: { xs: '1 1 140px', sm: 'none' } }}
          />

          {/* Reset Button */}
          {hasActiveFilters && (
            <Tooltip title="Reset all filters">
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                startIcon={<ResetIcon />}
                onClick={handleResetAllFilters}
                sx={{ height: 40, borderColor: 'divider', textTransform: 'none', px: 1.5 }}
              >
                Reset
              </Button>
            </Tooltip>
          )}

          {/* Bulk Assign Button */}
          {showAssign && selectedIds.length > 0 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAdd />}
              onClick={() => onBulkAssign?.(selectedIds)}
              sx={{ ml: 'auto', height: 40 }}
            >
              Assign {selectedIds.length} Trip{selectedIds.length > 1 ? 's' : ''}
            </Button>
          )}
        </Stack>

        {/* Active Filter Chips & Results Count */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Showing {filteredTrips.length} of {trips.length} trips
          </Typography>

          {searchTerm && (
            <Chip
              size="small"
              label={`Search: "${searchTerm}"`}
              onDelete={() => { setSearchTerm(''); setPage(0); }}
              color="primary"
              variant="outlined"
              sx={{ height: 24, fontSize: 12 }}
            />
          )}

          {executiveFilter !== 'All' && (
            <Chip
              size="small"
              label={`Executive: ${selectedExecutiveName}`}
              onDelete={() => { setExecutiveFilter('All'); setPage(0); }}
              color="primary"
              variant="outlined"
              sx={{ height: 24, fontSize: 12 }}
            />
          )}

          {statusFilter !== 'All' && (
            <Chip
              size="small"
              label={`Status: ${statusFilter}`}
              onDelete={() => handleStatusChange({ target: { value: 'All' } })}
              color="primary"
              variant="outlined"
              sx={{ height: 24, fontSize: 12 }}
            />
          )}

          {paymentFilter !== 'All' && (
            <Chip
              size="small"
              label={`Payment: ${paymentFilter}`}
              onDelete={() => { setPaymentFilter('All'); setPage(0); }}
              color="primary"
              variant="outlined"
              sx={{ height: 24, fontSize: 12 }}
            />
          )}

          {(travelStartDate || travelEndDate) && (
            <Chip
              size="small"
              label={`Travel: ${travelStartDate ? dayjs(travelStartDate).format('DD MMM YYYY') : 'Any'} → ${travelEndDate ? dayjs(travelEndDate).format('DD MMM YYYY') : 'Any'}`}
              onDelete={() => {
                setTravelStartDate('');
                setTravelEndDate('');
                setPage(0);
              }}
              color="primary"
              variant="outlined"
              sx={{ height: 24, fontSize: 12 }}
            />
          )}
        </Stack>
      </Box>

      {/* ── Table ── */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {showAssign && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedIds.length > 0 &&
                      filteredTrips.some(t => selectedIds.includes(t._id)) &&
                      !filteredTrips.every(t => selectedIds.includes(t._id))
                    }
                    checked={filteredTrips.length > 0 && filteredTrips.every(t => selectedIds.includes(t._id))}
                    onChange={toggleAll}
                    size="small"
                  />
                </TableCell>
              )}
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Booking Ref</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Guest</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Destination</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Confirm Date</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Travel Date</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Return</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50', textAlign: 'center' }}>Pax</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Executive</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Payment</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.50' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTrips.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 5 }}>
                  <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                    <FilterList sx={{ fontSize: 36, color: 'text.secondary' }} />
                    <Typography color="text.primary" fontWeight={600}>
                      {hasActiveFilters ? 'No trips match your search / filter criteria' : 'No trips found'}
                    </Typography>
                    {hasActiveFilters && (
                      <Button size="small" variant="outlined" onClick={handleResetAllFilters} sx={{ mt: 0.5, textTransform: 'none' }}>
                        Clear all filters
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {paginatedTrips.map((trip) => (
              <TableRow
                key={trip._id}
                hover
                selected={isSelected(trip._id)}
                sx={{ cursor: 'pointer' }}
                onClick={() => onRowClick?.(trip)}
              >
                {showAssign && (
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={isSelected(trip._id)}
                      onChange={() => toggleSelect(trip._id)}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Typography variant="caption" fontWeight={600} color="primary.main">
                    {trip.booking_ref || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      {trip.guest_id?.name || '—'}
                    </Typography>
                    {trip.guest_id?.phone && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {trip.guest_id?.phone}
                      </Typography>
                    )}
                    {trip.guest_id?.email && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 11 }}>
                        {trip.guest_id?.email}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{trip.destination || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {trip.confirm_date ? dayjs(trip.confirm_date).format('DD MMM YYYY hh:mm A') : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {trip.travel_date ? dayjs(trip.travel_date).format('DD MMM YYYY') : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {trip.return_date ? dayjs(trip.return_date).format('DD MMM YYYY') : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight={500}>{trip.total_pax || '—'}</Typography>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {trip.allocation && trip.allocation.length > 0 ? (
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {trip.allocation.map((alloc, idx) => (
                        <Box key={idx} display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: 'primary.light' }}>
                            {alloc.user_id?.fullName?.[0] || '?'}
                          </Avatar>
                          <Typography variant="caption" fontWeight={500}>
                            {alloc.user_id?.fullName || 'Unknown'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.disabled">Unassigned</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={trip.payment?.status || 'Pending'}
                    color={PAYMENT_COLORS[trip.payment?.status] || 'default'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={trip.trip_status}
                    color={STATUS_COLORS[trip.trip_status] || 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => onRowClick?.(trip)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {showAssign && (
                      <Tooltip title="Assign">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => { e.stopPropagation(); onAssign?.(trip); }}
                        >
                          <PersonAdd fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Table Pagination ── */}
      <TablePagination
        component="div"
        count={filteredTrips.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 20, 50, 100]}
        onPageChange={(_, p) => {
          setPage(p);
          onPageChange?.(p + 1);
        }}
        onRowsPerPageChange={(e) => {
          const newLimit = parseInt(e.target.value, 10);
          setRowsPerPage(newLimit);
          setPage(0);
          onPageChange?.(1, newLimit);
        }}
      />
    </Box>
  );
};

export default TripsTable;
